import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Upload, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { ReferenceListsImportPreview } from './ReferenceListsImportPreview';
import { runEndpoint } from '@/services/flow';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const STAGES = {
  UPLOAD: 'upload',
  VALIDATING: 'validating',
  PREVIEW: 'preview',
  IMPORTING: 'importing',
  COMPLETE: 'complete'
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['.xlsx', '.xls'];

/**
 * Multi-step wizard dialog for importing reference lists from Excel
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onOpenChange - Callback when dialog open state changes
 * @param {Function} props.onImportComplete - Callback when import successfully completes
 */
export function ReferenceListsImportDialog({ open, onOpenChange, onImportComplete }) {
  const { user } = useAuth();
  const [stage, setStage] = useState(STAGES.UPLOAD);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const resetDialog = () => {
    setStage(STAGES.UPLOAD);
    setSelectedFile(null);
    setUploading(false);
    setValidationResult(null);
    setImportResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetDialog();
    onOpenChange(false);
  };

  const validateFile = (file) => {
    const fileName = file.name.toLowerCase();
    const hasValidExtension = ALLOWED_EXTENSIONS.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      return {
        valid: false,
        error: `Invalid file type. Please upload ${ALLOWED_EXTENSIONS.join(' or ')} files only.`
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit.`
      };
    }

    return { valid: true };
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      setError(validation.error);
      setSelectedFile(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
  };

  const uploadFileToS3 = async (file) => {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const datePath = `year=${year}/month=${month}/day=${day}`;

      const urlResponse = await runEndpoint('cortex.vdd.s3.get_datasource_signed_url_put', {
        date: datePath,
        dataSourceId: 'reference-lists-import',
        filename: file.name
      });

      const presignedUrl = urlResponse.url;
      if (!presignedUrl) {
        throw new Error('Failed to get upload URL');
      }

      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream'
        },
        body: file
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3');
      }

      const s3Key = urlResponse.key || `data-governance/vdd/datasources/reference-lists-import/input/${datePath}/${file.name}`;
      return s3Key;
    } catch (err) {
      throw new Error(`Upload failed: ${err.message}`);
    }
  };

  const handleUploadAndValidate = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setStage(STAGES.VALIDATING);

    try {
      const s3Key = await uploadFileToS3(selectedFile);

      const result = await runEndpoint('cortex.vdd.reference_lists.import_with_validation', {
        filePath: s3Key,
        dryRun: true
      });

      setValidationResult(result);
      setStage(STAGES.PREVIEW);

      if (result.errors && result.errors.length > 0) {
        toast.error(`Validation found ${result.errors.length} error(s). Please review and fix before importing.`);
      } else {
        const totalItems =
          (result.preview?.lists?.toAdd?.length || 0) +
          (result.preview?.lists?.toUpdate?.length || 0) +
          (result.preview?.values?.toAdd?.length || 0) +
          (result.preview?.values?.toUpdate?.length || 0) +
          (result.preview?.synonyms?.toAdd?.length || 0);
        toast.success(`Ready to import ${totalItems} items.`);
      }
    } catch (err) {
      setError(err.message);
      setStage(STAGES.UPLOAD);
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setStage(STAGES.IMPORTING);

    try {
      const s3Key = await uploadFileToS3(selectedFile);

      const changedBy = (user?.first_name && user?.last_name)
        ? `${user.first_name} ${user.last_name}`
        : user?.email || 'Import';
      const response = await runEndpoint('cortex.vdd.reference_lists.import_with_validation', {
        filePath: s3Key,
        dryRun: false,
        changedBy
      });

      const result = response;
      setImportResult(result);
      setStage(STAGES.COMPLETE);

      const totalImported =
        (result.results?.listsAdded || 0) +
        (result.results?.listsUpdated || 0) +
        (result.results?.valuesAdded || 0) +
        (result.results?.valuesUpdated || 0) +
        (result.results?.synonymsAdded || 0);
      const importErrors = result.results?.errors || [];

      if (importErrors.length > 0) {
        if (totalImported > 0) {
          toast.warning(`Imported ${totalImported} items with ${importErrors.length} errors.`);
        } else {
          toast.error(`Import failed with ${importErrors.length} errors.`);
        }
      } else {
        toast.success(`Successfully imported ${totalImported} reference list items.`);
      }

      if (onImportComplete) {
        onImportComplete(result);
      }
    } catch (err) {
      setError(err.message);
      setStage(STAGES.PREVIEW);
      toast.error(err.message || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const renderUploadStage = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor="file-upload">Select File</Label>
        <Input
          id="file-upload"
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFileSelect}
          disabled={uploading}
        />
        {selectedFile && (
          <p className="text-sm text-muted-foreground">
            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
          </p>
        )}
      </div>

      <Alert className="border-blue-200 bg-blue-50">
        <AlertCircle className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          The Excel file should contain three sheets: "Reference Lists", "Values", and "Synonyms".
        </AlertDescription>
      </Alert>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  const renderValidatingStage = () => (
    <div className="space-y-6 py-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center">
          <h3 className="font-semibold text-lg">Validating File...</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Please wait while we validate your reference lists file.
          </p>
        </div>
        <Progress value={undefined} className="w-64" />
      </div>
    </div>
  );

  const renderPreviewStage = () => {
    const hasErrors = validationResult?.errors?.length > 0;
    const canImport = !hasErrors && validationResult?.preview;

    return (
      <div className="space-y-4">
        <Alert className={hasErrors ? '' : 'border-green-200 bg-green-50'}>
          {hasErrors ? (
            <>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Validation found errors that must be fixed before importing.
              </AlertDescription>
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                File validated successfully. Review the changes below and click "Confirm Import" to proceed.
              </AlertDescription>
            </>
          )}
        </Alert>

        <ReferenceListsImportPreview
          preview={validationResult?.preview}
          errors={validationResult?.errors || []}
          warnings={validationResult?.warnings || []}
        />

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    );
  };

  const renderImportingStage = () => (
    <div className="space-y-6 py-8">
      <div className="flex flex-col items-center justify-center space-y-4">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <div className="text-center">
          <h3 className="font-semibold text-lg">Importing Reference Lists...</h3>
          <p className="text-sm text-muted-foreground mt-2">
            Please wait while we import your reference lists. Do not close this dialog.
          </p>
        </div>
        <Progress value={undefined} className="w-64" />
      </div>
    </div>
  );

  const renderCompleteStage = () => {
    const listsAdded = importResult?.results?.listsAdded || 0;
    const listsUpdated = importResult?.results?.listsUpdated || 0;
    const valuesAdded = importResult?.results?.valuesAdded || 0;
    const valuesUpdated = importResult?.results?.valuesUpdated || 0;
    const synonymsAdded = importResult?.results?.synonymsAdded || 0;
    const errorsList = importResult?.results?.errors || [];
    const errorsCount = errorsList.length;
    const hasSuccess = listsAdded > 0 || listsUpdated > 0 || valuesAdded > 0 || valuesUpdated > 0 || synonymsAdded > 0;

    return (
      <div className="space-y-6 py-4">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className={`h-16 w-16 rounded-full flex items-center justify-center ${errorsCount > 0 && !hasSuccess ? 'bg-red-100' : errorsCount > 0 ? 'bg-yellow-100' : 'bg-green-100'}`}>
            {errorsCount > 0 && !hasSuccess ? (
              <AlertCircle className="h-10 w-10 text-red-600" />
            ) : errorsCount > 0 ? (
              <AlertCircle className="h-10 w-10 text-yellow-600" />
            ) : (
              <CheckCircle className="h-10 w-10 text-green-600" />
            )}
          </div>
          <div className="text-center">
            <h3 className="font-semibold text-xl">
              {errorsCount > 0 && !hasSuccess ? 'Import Failed' : errorsCount > 0 ? 'Import Completed with Errors' : 'Import Complete!'}
            </h3>
            <p className="text-sm text-muted-foreground mt-2">
              {errorsCount > 0 && !hasSuccess
                ? 'No items were imported. Please review the errors below.'
                : errorsCount > 0
                ? 'Some items were imported, but errors occurred.'
                : 'Your reference lists have been successfully imported.'}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 w-full max-w-2xl mt-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{listsAdded}</div>
              <div className="text-sm text-muted-foreground">Lists Added</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{listsUpdated}</div>
              <div className="text-sm text-muted-foreground">Lists Updated</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{valuesAdded}</div>
              <div className="text-sm text-muted-foreground">Values Added</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{synonymsAdded}</div>
              <div className="text-sm text-muted-foreground">Synonyms Added</div>
            </div>
            {errorsCount > 0 && (
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{errorsCount}</div>
                <div className="text-sm text-muted-foreground">Errors</div>
              </div>
            )}
          </div>

          {errorsCount > 0 && (
            <div className="w-full mt-4 max-h-60 overflow-y-auto">
              <div className="text-sm font-medium mb-2">Error Details:</div>
              <div className="space-y-2">
                {errorsList.slice(0, 20).map((err, idx) => (
                  <div key={idx} className="text-sm p-2 bg-red-50 border border-red-200 rounded">
                    <span className="font-medium">{err.item || err.list_name || err.value}:</span>{' '}
                    <span className="text-red-700">{err.error?.substring(0, 100)}{err.error?.length > 100 ? '...' : ''}</span>
                  </div>
                ))}
                {errorsList.length > 20 && (
                  <div className="text-sm text-muted-foreground">
                    ... and {errorsList.length - 20} more errors
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (stage) {
      case STAGES.UPLOAD:
        return renderUploadStage();
      case STAGES.VALIDATING:
        return renderValidatingStage();
      case STAGES.PREVIEW:
        return renderPreviewStage();
      case STAGES.IMPORTING:
        return renderImportingStage();
      case STAGES.COMPLETE:
        return renderCompleteStage();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    const hasErrors = validationResult?.errors?.length > 0;
    const canImport = !hasErrors && validationResult?.preview;

    switch (stage) {
      case STAGES.UPLOAD:
        return (
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleUploadAndValidate}
              disabled={!selectedFile || uploading}
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload & Validate
            </Button>
          </>
        );
      case STAGES.VALIDATING:
        return null;
      case STAGES.PREVIEW:
        return (
          <>
            <Button variant="outline" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={!canImport || uploading}
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Confirm Import
            </Button>
          </>
        );
      case STAGES.IMPORTING:
        return null;
      case STAGES.COMPLETE:
        return (
          <Button onClick={handleClose}>
            Close
          </Button>
        );
      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    switch (stage) {
      case STAGES.UPLOAD:
        return 'Import Reference Lists';
      case STAGES.VALIDATING:
        return 'Validating File';
      case STAGES.PREVIEW:
        return 'Review Import';
      case STAGES.IMPORTING:
        return 'Importing Reference Lists';
      case STAGES.COMPLETE:
        return 'Import Complete';
      default:
        return 'Import Reference Lists';
    }
  };

  const getDialogDescription = () => {
    switch (stage) {
      case STAGES.UPLOAD:
        return 'Upload an Excel file to import reference lists, values, and synonyms.';
      case STAGES.PREVIEW:
        return 'Review the changes that will be made to your reference lists.';
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="!w-[95vw] !max-w-[1400px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          {getDialogDescription() && (
            <DialogDescription>{getDialogDescription()}</DialogDescription>
          )}
        </DialogHeader>

        <div className="py-4">
          {renderContent()}
        </div>

        <DialogFooter>
          {renderFooter()}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
